// AllStudents.jsx
import React, { useMemo, useState } from "react";
import {
  Button,
  Card,
  Table,
  Modal,
  Form,
  Row,
  Col,
  Spinner,
  Container,
  Alert,
} from "react-bootstrap";
import { useStudentContext } from "../../contextProviders/studentContext";
import { useUser } from "../../contextProviders/userContext";
import NotificationIcon from "../../components/Notification";
import { FiEye, FiSmartphone, FiBell, FiMonitor, FiUsers, FiSend } from "react-icons/fi";
import AppShell from "../../components/AppShell";
import PageHeader from "../../components/PageHeader";
import AdminPagination from "../../components/AdminPagination";
import { useNavigate } from "react-router-dom";
import api from "../../api";

const AdminStudentsView = () => {
  const {
    students,
    loading,
    error,
    updateStudentRole,
    resetStudentPassword,
    createAdminUser,
    createOrganizationUser,
    reviewOrganization,
    deleteUserAccount,
  } = useStudentContext();
  const navigate = useNavigate();
  const [selectedStudent, setSelectedStudent] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { user } = useUser();

  const [searchTerm, setSearchTerm] = useState("");
  const [facultyFilter, setFacultyFilter] = useState("");
  const [degreeFilter, setDegreeFilter] = useState("");
  const [pwaFilter, setPwaFilter] = useState("all");
  
  // Separation Tab state: "student" or "admin"
  const [activeTab, setActiveTab] = useState("student");

  // Push Reminder Modal State
  const [showPushModal, setShowPushModal] = useState(false);
  const [pushData, setPushData] = useState({ title: "", message: "", target: "all", forceResend: false });
  const [pushLoading, setPushLoading] = useState(false);
  const [pushFeedback, setPushFeedback] = useState({ type: "", message: "" });

  // Admin Actions State
  const [resetPasswordVal, setResetPasswordVal] = useState("");
  const [actionFeedback, setActionFeedback] = useState({ type: "", message: "" });
  const [actionLoading, setActionLoading] = useState(false);

  // Create Admin State
  const [showCreateAdminModal, setShowCreateAdminModal] = useState(false);
  const [newAdminData, setNewAdminData] = useState({
    name: "",
    surname: "",
    email: "",
    password: "",
  });
  const [createAdminFeedback, setCreateAdminFeedback] = useState({ type: "", message: "" });
  const [createAdminLoading, setCreateAdminLoading] = useState(false);

  // Create Organisation State
  const [showCreateOrgModal, setShowCreateOrgModal] = useState(false);
  const [newOrgData, setNewOrgData] = useState({
    name: "",
    surname: "",
    email: "",
    password: "",
    organizationName: "",
  });
  const [createOrgFeedback, setCreateOrgFeedback] = useState({ type: "", message: "" });
  const [createOrgLoading, setCreateOrgLoading] = useState(false);

  // Role management + organisation approval state (for the currently open profile modal)
  const [roleSelectValue, setRoleSelectValue] = useState("");
  const [roleOrgNameValue, setRoleOrgNameValue] = useState("");
  const [reviewReason, setReviewReason] = useState("");

  // Delete User Confirmation State
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [deleteConfirmInput, setDeleteConfirmInput] = useState("");

  const handleCreateAdminSubmit = async (e) => {
    e.preventDefault();
    setCreateAdminLoading(true);
    setCreateAdminFeedback({ type: "", message: "" });

    // Validate email format
    const staffEmailRegex = /^[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+@wits\.ac\.za$/;
    if (!staffEmailRegex.test(newAdminData.email)) {
      setCreateAdminFeedback({
        type: "danger",
        message: "Invalid staff email. Staff emails must be in the format 'name.surname@wits.ac.za'.",
      });
      setCreateAdminLoading(false);
      return;
    }

    try {
      await createAdminUser(newAdminData);
      setCreateAdminFeedback({
        type: "success",
        message: "Admin account created successfully!",
      });
      setNewAdminData({ name: "", surname: "", email: "", password: "" });
      setTimeout(() => {
        setShowCreateAdminModal(false);
        setCreateAdminFeedback({ type: "", message: "" });
      }, 1500);
    } catch (err) {
      setCreateAdminFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to create admin account.",
      });
    } finally {
      setCreateAdminLoading(false);
    }
  };

  const handleCreateOrgSubmit = async (e) => {
    e.preventDefault();
    setCreateOrgLoading(true);
    setCreateOrgFeedback({ type: "", message: "" });

    try {
      await createOrganizationUser(newOrgData);
      setCreateOrgFeedback({
        type: "success",
        message: "Organisation account created successfully!",
      });
      setNewOrgData({ name: "", surname: "", email: "", password: "", organizationName: "" });
      setTimeout(() => {
        setShowCreateOrgModal(false);
        setCreateOrgFeedback({ type: "", message: "" });
      }, 1500);
    } catch (err) {
      setCreateOrgFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to create organisation account.",
      });
    } finally {
      setCreateOrgLoading(false);
    }
  };

  const handleDeleteUserSubmit = async () => {
    if (!selectedStudent) return;
    setActionLoading(true);
    setActionFeedback({ type: "", message: "" });
    try {
      await deleteUserAccount(selectedStudent._id);
      setActionFeedback({
        type: "success",
        message: "User account and associated data successfully deleted!",
      });
      setShowDeleteConfirm(false);
      setDeleteConfirmInput("");
      setTimeout(() => {
        handleCloseModal();
      }, 1500);
    } catch (err) {
      setActionFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to delete user account.",
      });
      setShowDeleteConfirm(false);
      setDeleteConfirmInput("");
    } finally {
      setActionLoading(false);
    }
  };

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 8;

  const handleViewStudent = (student) => {
    setSelectedStudent(student);
    setShowModal(true);
    setActionFeedback({ type: "", message: "" });
    setResetPasswordVal("");
    setRoleSelectValue(student.role || "student");
    setRoleOrgNameValue(student.organizationName || "");
    setReviewReason("");
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedStudent(null);
    setActionFeedback({ type: "", message: "" });
    setResetPasswordVal("");
  };

  const handleRoleUpdate = async () => {
    if (!selectedStudent || !roleSelectValue) return;
    if (roleSelectValue === "organization" && !roleOrgNameValue.trim()) {
      setActionFeedback({ type: "danger", message: "Organisation name is required." });
      return;
    }
    setActionLoading(true);
    setActionFeedback({ type: "", message: "" });
    try {
      await updateStudentRole(
        selectedStudent._id,
        roleSelectValue,
        roleSelectValue === "organization" ? roleOrgNameValue.trim() : undefined
      );
      setActionFeedback({
        type: "success",
        message: `Successfully updated user role to ${roleSelectValue}!`,
      });
      setSelectedStudent((prev) => ({
        ...prev,
        role: roleSelectValue,
        organizationName:
          roleSelectValue === "organization" ? roleOrgNameValue.trim() : prev.organizationName,
        approvalStatus: roleSelectValue === "organization" ? "approved" : prev.approvalStatus,
      }));
    } catch (err) {
      setActionFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to update user role.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleReviewOrganization = async (status) => {
    if (!selectedStudent) return;
    setActionLoading(true);
    setActionFeedback({ type: "", message: "" });
    try {
      const reason = status === "rejected" ? reviewReason.trim() : "";
      await reviewOrganization(selectedStudent._id, status, reason);
      setActionFeedback({
        type: "success",
        message: `Organisation account ${status}.`,
      });
      setSelectedStudent((prev) => ({ ...prev, approvalStatus: status, approvalReason: reason }));
      setReviewReason("");
    } catch (err) {
      setActionFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to update approval status.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  const handleAdminResetPassword = async (e) => {
    e.preventDefault();
    if (!selectedStudent || !resetPasswordVal) return;
    setActionLoading(true);
    setActionFeedback({ type: "", message: "" });
    try {
      await resetStudentPassword(selectedStudent._id, resetPasswordVal);
      setActionFeedback({
        type: "success",
        message: "User password has been successfully reset!",
      });
      setResetPasswordVal("");
    } catch (err) {
      setActionFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to reset password.",
      });
    } finally {
      setActionLoading(false);
    }
  };

  // PWA Adoption Calculations
  const studentUsers = useMemo(
    () => (students || []).filter((s) => (s.role || "student") === "student"),
    [students]
  );
  const totalStudentCount = studentUsers.length;
  const pwaAdoptedCount = studentUsers.filter((s) => s.pwaInstalled || s.pwaStandalone).length;
  const pushSubscribedCount = studentUsers.filter(
    (s) => s.hasPushSubscription || s.pwaPushPermission === "granted"
  ).length;
  const webOnlyCount = Math.max(0, totalStudentCount - pwaAdoptedCount);
  const pwaAdoptionRate =
    totalStudentCount > 0 ? Math.round((pwaAdoptedCount / totalStudentCount) * 100) : 0;

  const handleSendPushReminder = async (e) => {
    e.preventDefault();
    if (pushData.target !== "calendar_shifts" && (!pushData.title.trim() || !pushData.message.trim())) return;
    setPushLoading(true);
    setPushFeedback({ type: "", message: "" });
    try {
      const payload =
        pushData.target === "calendar_shifts"
          ? { target: "calendar_shifts", force: pushData.forceResend }
          : {
              title: pushData.title.trim(),
              message: pushData.message.trim(),
              target: pushData.target,
            };

      const res = await api.post("/push/send-reminder", payload);
      const sentCount = res.data.sent || 0;
      const shiftsChecked = res.data.shiftsChecked;

      setPushFeedback({
        type: "success",
        message:
          pushData.target === "calendar_shifts"
            ? `Calendar shift scan complete! Reminded ${res.data.studentsReminded || 0} volunteer(s) and ${res.data.leadersReminded || 0} leader(s) across ${shiftsChecked || 0} upcoming shift(s). (0 email quota consumed)`
            : `Push reminder dispatched to ${sentCount} active device(s)! (0 email quota consumed)`,
      });
      setPushData({ title: "", message: "", target: "all", forceResend: false });
      setTimeout(() => {
        setShowPushModal(false);
        setPushFeedback({ type: "", message: "" });
      }, 2500);
    } catch (err) {
      setPushFeedback({
        type: "danger",
        message: err.response?.data?.error || "Failed to send push reminder.",
      });
    } finally {
      setPushLoading(false);
    }
  };

  // Filter students based on active role tab and query terms
  const filteredStudents = students.filter((student) => {
    const roleMatch = (student.role || "student") === activeTab;
    const query = searchTerm.toLowerCase();
    const nameMatch = (student.name + " " + (student.surname || ""))
      .toLowerCase()
      .includes(query);
    const studentNumberMatch = (student.studentNumber || "")
      .toLowerCase()
      .includes(query);

    // Admins don't have degree/faculty constraints typically
    const facultyMatch = facultyFilter && activeTab === "student"
      ? student.faculty === facultyFilter
      : true;
    const degreeMatch = degreeFilter && activeTab === "student"
      ? student.degree === degreeFilter
      : true;

    let pwaMatch = true;
    if (activeTab === "student") {
      if (pwaFilter === "pwa") {
        pwaMatch = Boolean(student.pwaInstalled || student.pwaStandalone);
      } else if (pwaFilter === "push") {
        pwaMatch = Boolean(student.hasPushSubscription || student.pwaPushPermission === "granted");
      } else if (pwaFilter === "web") {
        pwaMatch = !student.pwaInstalled && !student.pwaStandalone;
      }
    }

    return roleMatch && (nameMatch || studentNumberMatch) && facultyMatch && degreeMatch && pwaMatch;
  });

  const uniqueFaculties = [
    ...new Set(students.filter(s => s.role === "student").map((student) => student.faculty)),
  ].filter(Boolean);
  
  const uniqueDegrees = [
    ...new Set(students.filter(s => s.role === "student").map((student) => student.degree)),
  ].filter(Boolean);

  // Pagination Calculations
  const totalPages = Math.ceil(filteredStudents.length / itemsPerPage);
  const activePage = currentPage > totalPages ? 1 : currentPage;
  const paginatedStudents = filteredStudents.slice(
    (activePage - 1) * itemsPerPage,
    activePage * itemsPerPage
  );

  const cardStyle = {
    backgroundColor: "var(--color-surface)",
    border: "1px solid var(--color-border)",
    boxShadow: "none",
    borderRadius: "var(--radius-md)",
  };

  if (loading) {
    return (
      <Container
        className="d-flex justify-content-center align-items-center"
        style={{ height: "100vh" }}>
        <Spinner animation="border" role="status">
          <span className="visually-hidden">Loading...</span>
        </Spinner>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <div className="error-message">{error}</div>
      </Container>
    );
  }

  return (
    <AppShell>
        <PageHeader
          title="Manage people"
          description="Find and manage student volunteers, organisations, and administrator access."
          actions={<NotificationIcon />}
        />

        {/* User Base Navigation Tabs */}
        <div className="d-flex border-bottom mb-3 bg-white p-2 rounded shadow-sm gap-2" style={{ backgroundColor: "var(--color-surface)", borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
          <Button
            onClick={() => {
              setActiveTab("student");
              setCurrentPage(1);
              setFacultyFilter("");
              setDegreeFilter("");
            }}
            className="fw-bold px-4 py-2 border-0"
            style={{ 
              borderRadius: "var(--radius-md)", 
              backgroundColor: activeTab === "student" ? "var(--color-primary)" : "rgba(27, 58, 135, 0.05)",
              color: activeTab === "student" ? "#fff" : "var(--color-text-secondary)",
              transition: "all var(--transition)"
            }}
          >
            Student Volunteers ({students.filter(s => (s.role || "student") === "student").length})
          </Button>
          <Button
            onClick={() => {
              setActiveTab("admin");
              setCurrentPage(1);
              setFacultyFilter("");
              setDegreeFilter("");
            }}
            className="fw-bold px-4 py-2 border-0"
            style={{ 
              borderRadius: "var(--radius-md)", 
              backgroundColor: activeTab === "admin" ? "var(--color-primary)" : "rgba(27, 58, 135, 0.05)",
              color: activeTab === "admin" ? "#fff" : "var(--color-text-secondary)",
              transition: "all var(--transition)"
            }}
          >
            System Administrators ({students.filter(s => s.role === "admin").length})
          </Button>
          <Button
            onClick={() => {
              setActiveTab("organization");
              setCurrentPage(1);
              setFacultyFilter("");
              setDegreeFilter("");
            }}
            className="fw-bold px-4 py-2 border-0"
            style={{
              borderRadius: "var(--radius-md)",
              backgroundColor: activeTab === "organization" ? "var(--color-primary)" : "rgba(27, 58, 135, 0.05)",
              color: activeTab === "organization" ? "#fff" : "var(--color-text-secondary)",
            }}
          >
            Organisations ({students.filter(s => s.role === "organization").length})
          </Button>
          {activeTab === "admin" && (
            <Button
              className="fw-bold px-3 py-2 btn-wcco-primary border-0 ms-auto"
              style={{ borderRadius: "var(--radius-md)" }}
              onClick={() => setShowCreateAdminModal(true)}
            >
              + Create Admin
            </Button>
          )}
          {activeTab === "organization" && (
            <Button
              className="fw-bold px-3 py-2 btn-wcco-primary border-0 ms-auto"
              style={{ borderRadius: "var(--radius-md)" }}
              onClick={() => setShowCreateOrgModal(true)}
            >
              + Create Organisation
            </Button>
          )}
        </div>

        {/* PWA Adoption KPI Strip */}
        {activeTab === "student" && (
          <div className="mb-4">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <div>
                <h5 className="fw-bold mb-0" style={{ color: "var(--color-primary)" }}>
                  Mobile PWA & Push Adoption
                </h5>
                <p className="text-muted small mb-0">
                  Real-time visibility into students who have installed the PWA and enabled push notifications.
                </p>
              </div>
              <Button
                className="fw-semibold px-3 py-2 border-0 d-flex align-items-center gap-2"
                style={{
                  backgroundColor: "var(--color-primary)",
                  color: "#fff",
                  borderRadius: "var(--radius-md)",
                }}
                onClick={() => setShowPushModal(true)}
              >
                <FiSend /> Broadcast Push Reminder
              </Button>
            </div>

            <Row className="g-3">
              <Col sm={6} lg={3}>
                <Card className="h-100 p-3 border-0 shadow-sm" style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)" }}>
                  <div className="d-flex align-items-center gap-3">
                    <div className="p-2.5 rounded text-primary" style={{ backgroundColor: "rgba(27, 58, 135, 0.08)", fontSize: "1.25rem" }}>
                      <FiUsers />
                    </div>
                    <div>
                      <span className="text-muted small d-block">Registered Students</span>
                      <strong className="fs-5" style={{ color: "var(--color-text)" }}>{totalStudentCount}</strong>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col sm={6} lg={3}>
                <Card className="h-100 p-3 border-0 shadow-sm" style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)" }}>
                  <div className="d-flex align-items-center gap-3">
                    <div className="p-2.5 rounded text-success" style={{ backgroundColor: "rgba(22, 163, 74, 0.1)", fontSize: "1.25rem" }}>
                      <FiSmartphone />
                    </div>
                    <div>
                      <span className="text-muted small d-block">Moved to PWA</span>
                      <strong className="fs-5 text-success">{pwaAdoptedCount} <span className="small text-muted fw-normal">({pwaAdoptionRate}%)</span></strong>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col sm={6} lg={3}>
                <Card className="h-100 p-3 border-0 shadow-sm" style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)" }}>
                  <div className="d-flex align-items-center gap-3">
                    <div className="p-2.5 rounded text-primary" style={{ backgroundColor: "rgba(27, 58, 135, 0.08)", fontSize: "1.25rem" }}>
                      <FiBell />
                    </div>
                    <div>
                      <span className="text-muted small d-block">Push Active</span>
                      <strong className="fs-5" style={{ color: "var(--color-primary)" }}>{pushSubscribedCount} <span className="small text-muted fw-normal">devices</span></strong>
                    </div>
                  </div>
                </Card>
              </Col>
              <Col sm={6} lg={3}>
                <Card className="h-100 p-3 border-0 shadow-sm" style={{ backgroundColor: "var(--color-surface)", border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)" }}>
                  <div className="d-flex align-items-center gap-3">
                    <div className="p-2.5 rounded text-secondary" style={{ backgroundColor: "rgba(100, 116, 139, 0.1)", fontSize: "1.25rem" }}>
                      <FiMonitor />
                    </div>
                    <div>
                      <span className="text-muted small d-block">Web Portal Only</span>
                      <strong className="fs-5 text-muted">{webOnlyCount}</strong>
                    </div>
                  </div>
                </Card>
              </Col>
            </Row>
          </div>
        )}

        {/* Listing Container */}
        <Card className="mb-4" style={cardStyle}>
          <Card.Body className="p-4">
            {/* Filters Row */}
            <Row className="mb-4 g-3 align-items-center">
              <Col md={activeTab === "student" ? 3 : 12}>
                <Form.Control
                  type="text"
                  placeholder={activeTab === "student" ? "Search by student name or student number..." : "Search by admin name..."}
                  value={searchTerm}
                  onChange={(e) => {
                    setSearchTerm(e.target.value);
                    setCurrentPage(1); // Reset to page 1 on search
                  }}
                  style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 14px", backgroundColor: "var(--color-surface)", color: "var(--color-text)" }}
                />
              </Col>
              
              {activeTab === "student" && (
                <>
                  <Col md={3}>
                    <Form.Select
                      value={pwaFilter}
                      onChange={(e) => {
                        setPwaFilter(e.target.value);
                        setCurrentPage(1);
                      }}
                      style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 14px", backgroundColor: "var(--color-surface)", color: "var(--color-text)" }}
                    >
                      <option value="all">All Platforms</option>
                      <option value="pwa">📱 PWA Users Only</option>
                      <option value="push">🔔 Push Subscribed Only</option>
                      <option value="web">💻 Web Only</option>
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select
                      value={facultyFilter}
                      onChange={(e) => {
                        setFacultyFilter(e.target.value);
                        setCurrentPage(1); // Reset to page 1 on filter
                      }}
                      style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 14px", backgroundColor: "var(--color-surface)", color: "var(--color-text)" }}>
                      <option value="">All Faculties</option>
                      {uniqueFaculties.map((faculty) => (
                        <option key={faculty} value={faculty}>
                          {faculty}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                  <Col md={3}>
                    <Form.Select
                      value={degreeFilter}
                      onChange={(e) => {
                        setDegreeFilter(e.target.value);
                        setCurrentPage(1); // Reset to page 1 on filter
                      }}
                      style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-md)", padding: "10px 14px", backgroundColor: "var(--color-surface)", color: "var(--color-text)" }}>
                      <option value="">All Degrees</option>
                      {uniqueDegrees.map((degree) => (
                        <option key={degree} value={degree}>
                          {degree}
                        </option>
                      ))}
                    </Form.Select>
                  </Col>
                </>
              )}
            </Row>

            {filteredStudents.length > 0 ? (
              <>
                <div className="table-responsive">
                  <Table hover align="middle" className="mb-0" style={{ minWidth: "760px" }}>
                  <thead style={{ backgroundColor: "rgba(27, 58, 135, 0.05)", borderBottom: "2px solid var(--color-border)" }}>
                    <tr>
                      <th className="text-start py-3" style={{ borderTopLeftRadius: "var(--radius-md)", color: "var(--color-primary)", fontWeight: "600" }}>Name</th>
                      <th className="text-start py-3" style={{ color: "var(--color-primary)", fontWeight: "600" }}>Surname</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "600" }}>{activeTab === "student" ? "Student Number" : "System ID"}</th>
                      <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "600" }}>Email</th>
                      {activeTab === "student" && (
                        <>
                          <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "600" }}>PWA App</th>
                          <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "600" }}>Push Notifications</th>
                        </>
                      )}
                      {activeTab === "organization" && (
                        <th className="py-3" style={{ color: "var(--color-primary)", fontWeight: "600" }}>Approval</th>
                      )}
                      <th className="text-end py-3" style={{ borderTopRightRadius: "var(--radius-md)", paddingRight: "20px", color: "var(--color-primary)", fontWeight: "600" }}>Actions</th>
                    </tr>
                  </thead>
                  <tbody>
                    {paginatedStudents.map((student) => (
                      <tr 
                        key={student._id}
                        onClick={() => handleViewStudent(student)}
                        style={{ cursor: "pointer", transition: "background-color var(--transition)" }}
                        className="align-middle"
                      >
                        <td className="text-start fw-bold py-3" style={{ color: "var(--color-text)" }}>
                          {student.organizationName || student.name}
                        </td>
                        <td className="text-start py-3 fw-semibold" style={{ color: "var(--color-text-secondary)" }}>
                          {student.surname}
                        </td>
                        <td className="py-3 fw-bold" style={{ color: "var(--color-primary)" }}>
                          {student.studentNumber || student._id.toString().substring(0, 8).toUpperCase()}
                        </td>
                        <td className="py-3 text-muted" style={{ color: "var(--color-text-muted)" }}>
                          {student.email}
                        </td>
                        {activeTab === "student" && (
                          <>
                            <td className="py-3">
                              {student.pwaInstalled || student.pwaStandalone ? (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: "rgba(22, 163, 74, 0.12)",
                                    color: "#15803d",
                                    border: "1px solid rgba(22, 163, 74, 0.3)",
                                    fontWeight: "600",
                                    padding: "5px 9px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  📱 PWA {student.pwaPlatform ? `(${student.pwaPlatform})` : ""}
                                </span>
                              ) : (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: "rgba(100, 116, 139, 0.1)",
                                    color: "#64748b",
                                    border: "1px solid rgba(100, 116, 139, 0.25)",
                                    fontWeight: "600",
                                    padding: "5px 9px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  💻 Web
                                </span>
                              )}
                            </td>
                            <td className="py-3">
                              {student.hasPushSubscription || student.pwaPushPermission === "granted" ? (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: "rgba(27, 58, 135, 0.1)",
                                    color: "var(--color-primary)",
                                    border: "1px solid var(--color-border)",
                                    fontWeight: "600",
                                    padding: "5px 9px",
                                    borderRadius: "4px",
                                  }}
                                  title={student.subscriptionCount ? `${student.subscriptionCount} active push device(s)` : "Push notifications active"}
                                >
                                  🔔 Active
                                </span>
                              ) : (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: "rgba(148, 163, 184, 0.1)",
                                    color: "var(--color-text-muted)",
                                    border: "1px solid var(--color-border)",
                                    fontWeight: "500",
                                    padding: "5px 9px",
                                    borderRadius: "4px",
                                  }}
                                >
                                  🔕 Off
                                </span>
                              )}
                            </td>
                          </>
                        )}
                        {activeTab === "organization" && (
                          <td className="py-3">
                            <span className="badge-primary">
                              {(student.approvalStatus || "approved").toUpperCase()}
                            </span>
                          </td>
                        )}
                        <td className="text-end py-3" style={{ paddingRight: "20px" }} onClick={(e) => e.stopPropagation()}>
                          <Button
                            variant="light"
                            size="sm"
                            className="fw-semibold px-3 py-1.5"
                            style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)", color: "var(--color-primary)", backgroundColor: "var(--color-surface)" }}
                            onClick={() => handleViewStudent(student)}>
                            <FiEye className="me-1" /> View Profile
                          </Button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  </Table>
                </div>

                <AdminPagination
                  currentPage={activePage}
                  totalPages={totalPages}
                  totalItems={filteredStudents.length}
                  itemsPerPage={itemsPerPage}
                  itemLabel="accounts"
                  onPageChange={setCurrentPage}
                />
              </>
            ) : (
              <div className="text-center py-5">
                <p className="text-muted mb-0">No registered accounts found in this category.</p>
              </div>
            )}

            {/* View Profile Modal */}
            {selectedStudent && (
              <Modal show={showModal} onHide={handleCloseModal} size="lg" centered>
                <Modal.Header closeButton style={{ borderBottom: "1px solid var(--color-border)" }} className="pb-3">
                  <Modal.Title className="fw-bold" style={{ color: "var(--color-primary)" }}>
                    Account Profile: {selectedStudent.name} {selectedStudent.surname}
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3 modal-body-scroll">
                  <Card className="border-0 shadow-none" style={{ backgroundColor: "var(--color-bg)", borderRadius: "var(--radius-lg)" }}>
                    <Card.Body className="p-4">
                      <h5 className="fw-bold mb-3" style={{ color: "var(--color-primary)" }}>Personal & Role Info</h5>
                      <Table hover size="sm" className="mb-4">
                        <tbody>
                          <tr>
                            <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Security Privilege</th>
                            <td className="py-2">
                              <span className={`px-2 py-1 fw-bold ${selectedStudent.role === "admin" ? "badge-red" : "badge-primary"}`} style={{ fontSize: "0.75rem", borderRadius: "12px" }}>
                                {selectedStudent.role?.toUpperCase() || "STUDENT"}
                              </span>
                            </td>
                          </tr>
                          {selectedStudent.role === "student" && (
                            <tr>
                              <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Student Number</th>
                              <td className="py-2 fw-bold" style={{ color: "var(--color-primary)" }}>{selectedStudent.studentNumber}</td>
                            </tr>
                          )}
                          {selectedStudent.role === "organization" && (
                            <>
                              <tr>
                                <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Organisation Name</th>
                                <td className="py-2 fw-bold" style={{ color: "var(--color-primary)" }}>{selectedStudent.organizationName || "N/A"}</td>
                              </tr>
                              <tr>
                                <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Approval Status</th>
                                <td className="py-2">
                                  <span className="badge-primary">
                                    {(selectedStudent.approvalStatus || "approved").toUpperCase()}
                                  </span>
                                  {selectedStudent.approvalStatus === "rejected" && selectedStudent.approvalReason && (
                                    <div className="text-muted small mt-1">Reason: {selectedStudent.approvalReason}</div>
                                  )}
                                </td>
                              </tr>
                            </>
                          )}
                          <tr>
                            <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Email Address</th>
                            <td className="py-2" style={{ color: "var(--color-text)" }}>{selectedStudent.email}</td>
                          </tr>
                          <tr>
                            <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Contact Number</th>
                            <td className="py-2" style={{ color: "var(--color-text)" }}>{selectedStudent.contactNumber || "N/A"}</td>
                          </tr>
                          {selectedStudent.role === "student" && (
                            <>
                              <tr>
                                <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Degree Program</th>
                                <td className="py-2 fw-semibold" style={{ color: "var(--color-text-secondary)" }}>{selectedStudent.degree}</td>
                              </tr>
                              <tr>
                                <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Faculty</th>
                                <td className="py-2" style={{ color: "var(--color-text)" }}>{selectedStudent.faculty}</td>
                              </tr>
                              <tr>
                                <th className="py-2 text-muted" style={{ fontWeight: "500" }}>Volunteering Interests</th>
                                <td className="py-2" style={{ color: "var(--color-text-secondary)" }}>
                                  {selectedStudent.volunteeringInterests?.join(", ") || "None specified"}
                                </td>
                              </tr>
                            </>
                          )}
                        </tbody>
                      </Table>

                      {/* Mobile PWA & Push Status for Students */}
                      {selectedStudent.role === "student" && (
                        <Card
                          className="mb-4 shadow-none"
                          style={{
                            borderRadius: "var(--radius-md)",
                            border: "1px solid var(--color-border)",
                            backgroundColor: "var(--color-surface)",
                          }}
                        >
                          <Card.Body className="p-3">
                            <div className="d-flex justify-content-between align-items-center mb-2">
                              <h6 className="fw-bold mb-0 text-dark" style={{ color: "var(--color-primary)" }}>
                                Mobile PWA & Notification Status
                              </h6>
                              {selectedStudent.pwaInstalled || selectedStudent.pwaStandalone ? (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: "rgba(22, 163, 74, 0.12)",
                                    color: "#15803d",
                                    border: "1px solid rgba(22, 163, 74, 0.3)",
                                    borderRadius: "4px",
                                    fontWeight: "600",
                                  }}
                                >
                                  Moved to PWA
                                </span>
                              ) : (
                                <span
                                  className="badge"
                                  style={{
                                    backgroundColor: "rgba(100, 116, 139, 0.1)",
                                    color: "#64748b",
                                    border: "1px solid rgba(100, 116, 139, 0.25)",
                                    borderRadius: "4px",
                                    fontWeight: "600",
                                  }}
                                >
                                  Web Portal Only
                                </span>
                              )}
                            </div>
                            <Row className="g-2 small text-start">
                              <Col sm={6}>
                                <span className="text-muted">Client App:</span>{" "}
                                <strong>
                                  {selectedStudent.pwaStandalone
                                    ? "Installed Mobile App (Standalone PWA)"
                                    : selectedStudent.pwaInstalled
                                    ? "PWA Web App"
                                    : "Desktop Browser"}
                                </strong>
                              </Col>
                              <Col sm={6}>
                                <span className="text-muted">Platform:</span>{" "}
                                <strong>{selectedStudent.pwaPlatform || "Web"}</strong>
                              </Col>
                              <Col sm={6}>
                                <span className="text-muted">Push Notifications:</span>{" "}
                                <strong style={{ color: selectedStudent.hasPushSubscription ? "#15803d" : "inherit" }}>
                                  {selectedStudent.hasPushSubscription
                                    ? `Subscribed (${selectedStudent.subscriptionCount || 1} active device)`
                                    : "Not Subscribed"}
                                </strong>
                              </Col>
                              <Col sm={6}>
                                <span className="text-muted">Last Active:</span>{" "}
                                <strong>
                                  {selectedStudent.pwaLastActiveAt
                                    ? new Date(selectedStudent.pwaLastActiveAt).toLocaleString("en-ZA", {
                                        dateStyle: "medium",
                                        timeStyle: "short",
                                      })
                                    : "No PWA session recorded yet"}
                                </strong>
                              </Col>
                            </Row>
                          </Card.Body>
                        </Card>
                      )}

                      {/* Display Active Participations only for Students */}
                      {selectedStudent.role === "student" && (
                        <>
                          <h5 className="fw-bold mb-3 mt-4" style={{ color: "var(--color-primary)" }}>Active Participations</h5>
                          {selectedStudent.participations && selectedStudent.participations.length > 0 ? (
                            selectedStudent.participations.map((participation) => (
                              <Card key={participation._id} className="mb-3 shadow-none" style={{ borderRadius: "var(--radius-md)", border: "1px solid var(--color-border)", backgroundColor: "var(--color-surface)" }}>
                                <Card.Body className="p-3">
                                  <h6 className="fw-bold text-dark mb-2" style={{ fontSize: "1.1rem", color: "var(--color-text)" }}>
                                    {participation.project?.name || "Volunteer Program"}
                                  </h6>
                                  <div className="d-flex justify-content-between text-muted small mb-2">
                                    <span>Role: <strong>{participation.role}</strong></span>
                                    <span>Joined: <strong>{new Date(participation.dateJoined).toLocaleDateString()}</strong></span>
                                  </div>
                                  <div className="d-flex gap-2 flex-wrap">
                                    <span className="badge-teal px-2 py-1" style={{ fontSize: "0.75rem", borderRadius: "12px" }}>Approved: {participation.approvedHours} hrs</span>
                                    <span className="badge-gold px-2 py-1" style={{ fontSize: "0.75rem", borderRadius: "12px" }}>Pending: {participation.pendingHours} hrs</span>
                                    <span className="badge-primary px-2 py-1" style={{ fontSize: "0.75rem", borderRadius: "12px" }}>Total: {participation.totalHours} hrs</span>
                                  </div>
                                </Card.Body>
                              </Card>
                            ))
                          ) : (
                            <p className="text-muted small">No active program participations registered.</p>
                          )}
                        </>
                      )}

                      {/* Direct Consultation Panel */}
                      <div className="d-flex justify-content-between align-items-center p-3 mb-4 rounded border bg-white" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                        <div className="text-start">
                          <h6 className="fw-bold text-dark mb-1">Direct Consultation</h6>
                          <p className="text-muted small mb-0">Start a private consultation message thread with this student.</p>
                        </div>
                        <Button 
                          className="fw-bold px-3 py-2 border-0 btn-wcco-teal"
                          onClick={() => {
                            setShowModal(false);
                            navigate(`/messages?userId=${selectedStudent._id}`);
                          }}
                        >
                          Send Message
                        </Button>
                      </div>

                      {/* Administrative Security Actions Section */}
                      <hr className="my-4" style={{ color: "var(--color-border)" }} />
                      <h5 className="fw-bold mb-3 text-danger">Administrative System Security</h5>
                      
                      {actionFeedback.message && (
                        <Alert variant={actionFeedback.type} className="py-2.5 px-3 mb-3 small text-start">
                          {actionFeedback.message}
                        </Alert>
                      )}

                      {selectedStudent.role === "organization" && selectedStudent.approvalStatus !== "approved" && (
                        <div className="p-3 mb-3 border rounded text-start bg-white" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                          <h6 className="fw-bold text-dark small mb-1">Organisation Approval</h6>
                          <p className="text-muted small mb-3">
                            This organisation cannot sign in until its registration is approved.
                          </p>
                          <Form.Control
                            as="textarea"
                            rows={2}
                            placeholder="Reason for rejection (optional, shown to the applicant)"
                            value={reviewReason}
                            onChange={(e) => setReviewReason(e.target.value)}
                            className="mb-2"
                            style={{ borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border)" }}
                          />
                          <div className="d-flex gap-2">
                            <Button
                              size="sm"
                              disabled={actionLoading}
                              onClick={() => handleReviewOrganization("approved")}
                              className="fw-bold flex-grow-1 py-2 border-0"
                              style={{ backgroundColor: "var(--color-primary)", color: "#fff", borderRadius: "var(--radius-sm)" }}
                            >
                              Approve
                            </Button>
                            <Button
                              size="sm"
                              disabled={actionLoading}
                              onClick={() => handleReviewOrganization("rejected")}
                              variant="outline-secondary"
                              className="fw-bold flex-grow-1 py-2"
                              style={{ color: "var(--color-text)", borderColor: "var(--color-border)", borderRadius: "var(--radius-sm)" }}
                            >
                              Reject
                            </Button>
                          </div>
                        </div>
                      )}

                      <div className="d-flex flex-column flex-md-row gap-3 align-items-md-stretch">
                        {/* Role Management */}
                        <div className="flex-grow-1 p-3 border rounded text-start bg-white d-flex flex-column justify-content-between" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                          <div>
                            <h6 className="fw-bold text-dark small mb-1">User Role</h6>
                            <p className="text-muted small mb-3">
                              Change this account's role and system access.
                            </p>
                          </div>
                          <div>
                            <Form.Select
                              size="sm"
                              value={roleSelectValue}
                              disabled={actionLoading || selectedStudent._id === user?._id}
                              onChange={(e) => setRoleSelectValue(e.target.value)}
                              className="mb-2"
                              style={{ borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border)" }}
                            >
                              <option value="student">Student</option>
                              <option value="admin">Admin</option>
                              <option value="organization">Organisation</option>
                            </Form.Select>
                            {roleSelectValue === "organization" && (
                              <Form.Control
                                type="text"
                                size="sm"
                                placeholder="Organisation name"
                                value={roleOrgNameValue}
                                onChange={(e) => setRoleOrgNameValue(e.target.value)}
                                className="mb-2"
                                style={{ borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border)" }}
                              />
                            )}
                            <Button
                              size="sm"
                              disabled={
                                actionLoading ||
                                selectedStudent._id === user?._id ||
                                (roleSelectValue === selectedStudent.role &&
                                  (roleSelectValue !== "organization" ||
                                    roleOrgNameValue.trim() === (selectedStudent.organizationName || "")))
                              }
                              onClick={handleRoleUpdate}
                              className="fw-bold w-100 py-2 border-0"
                              style={{
                                backgroundColor: "var(--color-primary)",
                                color: "#fff",
                                borderRadius: "var(--radius-sm)"
                              }}
                            >
                              Update Role
                            </Button>
                            {selectedStudent._id === user?._id && (
                              <span className="text-danger mt-2 d-block text-center" style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                                You cannot change your own role.
                              </span>
                            )}
                          </div>
                        </div>

                        {/* Reset User Password */}
                        <div className="flex-grow-1 p-3 border rounded text-start bg-white d-flex flex-column justify-content-between" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                          <div>
                            <h6 className="fw-bold text-dark small mb-1">Administrative Force-Reset Password</h6>
                            <p className="text-muted small mb-3">
                              Update user's password directly. Use at least 8 characters with uppercase,
                              lowercase, a number, and a special character.
                            </p>
                          </div>
                          <Form onSubmit={handleAdminResetPassword} className="d-flex gap-2">
                            <Form.Control
                              type="password"
                              placeholder="Type new password..."
                              size="sm"
                              value={resetPasswordVal}
                              onChange={(e) => setResetPasswordVal(e.target.value)}
                              minLength={8}
                              required
                              style={{ borderRadius: "var(--radius-sm)", border: "1px solid var(--color-border)" }}
                            />
                            <Button 
                              type="submit" 
                              size="sm" 
                              disabled={actionLoading}
                              className="fw-bold px-3 shrink-0 border-0"
                              style={{ backgroundColor: "var(--color-red)", color: "#fff", borderRadius: "var(--radius-sm)" }}
                            >
                              Reset
                            </Button>
                          </Form>
                        </div>

                        {/* Delete User Account */}
                        <div className="flex-grow-1 p-3 border rounded text-start bg-white d-flex flex-column justify-content-between" style={{ borderColor: "var(--color-border)", borderRadius: "var(--radius-md)" }}>
                          <div>
                            <h6 className="fw-bold text-dark small mb-1">Delete User Account</h6>
                            <p className="text-muted small mb-3">
                              Permanently remove this account and associated hours/logs.
                            </p>
                          </div>
                          <div>
                            <Button
                              size="sm"
                              disabled={actionLoading || selectedStudent._id === user?._id}
                              onClick={() => setShowDeleteConfirm(true)}
                              className="fw-bold w-100 py-2 border-0"
                              style={{ 
                                backgroundColor: "var(--color-red)",
                                color: "#fff",
                                borderRadius: "var(--radius-sm)"
                              }}
                            >
                              Delete Account
                            </Button>
                            {selectedStudent._id === user?._id && (
                              <span className="text-danger mt-2 d-block text-center" style={{ fontSize: "0.75rem", fontWeight: "500" }}>
                                Self-deletion is locked.
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                    </Card.Body>
                  </Card>
                </Modal.Body>
                <Modal.Footer className="border-0">
                  <Button variant="secondary" className="px-4" style={{ borderRadius: "var(--radius-sm)" }} onClick={handleCloseModal}>
                    Close
                  </Button>
                </Modal.Footer>
              </Modal>
            )}

            {/* Create Admin Modal */}
            <Modal show={showCreateAdminModal} onHide={() => { if (!createAdminLoading) setShowCreateAdminModal(false); }} centered>
              <Modal.Header closeButton style={{ borderBottom: "1px solid var(--color-border)" }} className="pb-3">
                <Modal.Title className="fw-bold" style={{ color: "var(--color-primary)" }}>
                  Create Admin Account
                </Modal.Title>
              </Modal.Header>
              <Form onSubmit={handleCreateAdminSubmit}>
                <Modal.Body className="pt-3">
                  {createAdminFeedback.message && (
                    <Alert variant={createAdminFeedback.type} className="py-2.5 px-3 mb-3 small text-start">
                      {createAdminFeedback.message}
                    </Alert>
                  )}
                  <Row className="g-3 text-start">
                    <Col xs={12} sm={6}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500 }}>First Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={newAdminData.name}
                          onChange={(e) => setNewAdminData({ ...newAdminData, name: e.target.value })}
                          required
                          placeholder="Enter first name"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500 }}>Surname</Form.Label>
                        <Form.Control
                          type="text"
                          name="surname"
                          value={newAdminData.surname}
                          onChange={(e) => setNewAdminData({ ...newAdminData, surname: e.target.value })}
                          required
                          placeholder="Enter surname"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500 }}>Staff Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={newAdminData.email}
                          onChange={(e) => setNewAdminData({ ...newAdminData, email: e.target.value })}
                          required
                          placeholder="name.surname@wits.ac.za"
                          pattern="^[a-zA-Z0-9.-]+\.[a-zA-Z0-9.-]+@wits\.ac\.za$"
                          title="Staff email must be in the format name.surname@wits.ac.za"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                        <Form.Text className="text-muted" style={{ fontSize: "0.78rem" }}>
                          Must be a valid Wits staff email domain.
                        </Form.Text>
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500 }}>Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={newAdminData.password}
                          onChange={(e) => setNewAdminData({ ...newAdminData, password: e.target.value })}
                          required
                          minLength={6}
                          placeholder="Enter account password (min 6 chars)"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                </Modal.Body>
                <Modal.Footer className="border-0">
                  <Button
                    variant="secondary"
                    className="px-4"
                    disabled={createAdminLoading}
                    style={{ borderRadius: "var(--radius-sm)" }}
                    onClick={() => setShowCreateAdminModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createAdminLoading}
                    className="px-4 border-0 btn-wcco-primary"
                    style={{ borderRadius: "var(--radius-sm)" }}
                  >
                    {createAdminLoading ? "Creating..." : "Create Account"}
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>

            {/* Create Organisation Modal */}
            <Modal show={showCreateOrgModal} onHide={() => { if (!createOrgLoading) setShowCreateOrgModal(false); }} centered>
              <Modal.Header closeButton style={{ borderBottom: "1px solid var(--color-border)" }} className="pb-3">
                <Modal.Title className="fw-bold" style={{ color: "var(--color-primary)" }}>
                  Create Organisation Account
                </Modal.Title>
              </Modal.Header>
              <Form onSubmit={handleCreateOrgSubmit}>
                <Modal.Body className="pt-3">
                  {createOrgFeedback.message && (
                    <Alert variant={createOrgFeedback.type} className="py-2.5 px-3 mb-3 small text-start">
                      {createOrgFeedback.message}
                    </Alert>
                  )}
                  <Row className="g-3 text-start">
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500 }}>Organisation Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="organizationName"
                          value={newOrgData.organizationName}
                          onChange={(e) => setNewOrgData({ ...newOrgData, organizationName: e.target.value })}
                          required
                          placeholder="Enter organisation name"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500 }}>Contact First Name</Form.Label>
                        <Form.Control
                          type="text"
                          name="name"
                          value={newOrgData.name}
                          onChange={(e) => setNewOrgData({ ...newOrgData, name: e.target.value })}
                          required
                          placeholder="Enter first name"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12} sm={6}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500 }}>Contact Surname</Form.Label>
                        <Form.Control
                          type="text"
                          name="surname"
                          value={newOrgData.surname}
                          onChange={(e) => setNewOrgData({ ...newOrgData, surname: e.target.value })}
                          required
                          placeholder="Enter surname"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500 }}>Contact Email Address</Form.Label>
                        <Form.Control
                          type="email"
                          name="email"
                          value={newOrgData.email}
                          onChange={(e) => setNewOrgData({ ...newOrgData, email: e.target.value })}
                          required
                          placeholder="contact@organisation.org"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                      </Form.Group>
                    </Col>
                    <Col xs={12}>
                      <Form.Group>
                        <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500 }}>Password</Form.Label>
                        <Form.Control
                          type="password"
                          name="password"
                          value={newOrgData.password}
                          onChange={(e) => setNewOrgData({ ...newOrgData, password: e.target.value })}
                          required
                          minLength={6}
                          placeholder="Enter account password (min 6 chars)"
                          style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                        />
                      </Form.Group>
                    </Col>
                  </Row>
                  <Form.Text className="text-muted d-block mt-2" style={{ fontSize: "0.78rem" }}>
                    Admin-created organisation accounts are pre-approved and can sign in immediately.
                  </Form.Text>
                </Modal.Body>
                <Modal.Footer className="border-0">
                  <Button
                    variant="secondary"
                    className="px-4"
                    disabled={createOrgLoading}
                    style={{ borderRadius: "var(--radius-sm)" }}
                    onClick={() => setShowCreateOrgModal(false)}
                  >
                    Cancel
                  </Button>
                  <Button
                    type="submit"
                    disabled={createOrgLoading}
                    className="px-4 border-0 btn-wcco-primary"
                    style={{ borderRadius: "var(--radius-sm)" }}
                  >
                    {createOrgLoading ? "Creating..." : "Create Account"}
                  </Button>
                </Modal.Footer>
              </Form>
            </Modal>

            {/* Delete User Confirmation Modal */}
            {selectedStudent && (
              <Modal show={showDeleteConfirm} onHide={() => { if (!actionLoading) setShowDeleteConfirm(false); }} centered size="md">
                <Modal.Header closeButton style={{ borderBottom: "1px solid var(--color-border)" }} className="pb-3">
                  <Modal.Title className="fw-bold text-danger">
                    Confirm Account Deletion
                  </Modal.Title>
                </Modal.Header>
                <Modal.Body className="pt-3">
                  <p className="text-start mb-3">
                    Are you sure you want to permanently delete the account for <strong>{selectedStudent.name} {selectedStudent.surname}</strong> ({selectedStudent.email})?
                  </p>
                  <p className="text-start text-danger mb-4 small fw-semibold">
                    WARNING: This action is irreversible. All logged volunteering hours, history, and records associated with this account will be permanently erased.
                  </p>
                  <Form onSubmit={(e) => { e.preventDefault(); if (deleteConfirmInput === "DELETE") handleDeleteUserSubmit(); }}>
                    <Form.Group className="mb-3 text-start">
                      <Form.Label style={{ fontSize: "0.85rem", fontWeight: 500 }}>Type <strong>DELETE</strong> to confirm:</Form.Label>
                      <Form.Control
                        type="text"
                        placeholder="Type DELETE..."
                        value={deleteConfirmInput}
                        onChange={(e) => setDeleteConfirmInput(e.target.value)}
                        required
                        style={{ borderRadius: "var(--radius-md)", borderColor: "var(--color-border)" }}
                      />
                    </Form.Group>
                    <div className="d-flex justify-content-end gap-2">
                      <Button
                        variant="secondary"
                        disabled={actionLoading}
                        style={{ borderRadius: "var(--radius-sm)" }}
                        onClick={() => { setShowDeleteConfirm(false); setDeleteConfirmInput(""); }}
                      >
                        Cancel
                      </Button>
                      <Button
                        type="submit"
                        disabled={actionLoading || deleteConfirmInput !== "DELETE"}
                        style={{ backgroundColor: "var(--color-red)", color: "#fff", borderRadius: "var(--radius-sm)", border: "none" }}
                      >
                        {actionLoading ? "Deleting..." : "Permanently Delete"}
                      </Button>
                    </div>
                  </Form>
                </Modal.Body>
              </Modal>
            )}

            {/* Broadcast Push Reminder Modal */}
            <Modal show={showPushModal} onHide={() => setShowPushModal(false)} centered>
              <Modal.Header closeButton style={{ borderBottom: "1px solid var(--color-border)" }}>
                <Modal.Title className="fw-bold" style={{ color: "var(--color-primary)" }}>
                  Broadcast Push Notification
                </Modal.Title>
              </Modal.Header>
              <Modal.Body className="p-4">
                <p className="text-muted small mb-3">
                  Dispatches real-time Web Push alerts to student devices. Delivered with <strong>0 email quota consumed</strong> and reaches all volunteers on the PWA immediately.
                </p>

                {pushFeedback.message && (
                  <Alert variant={pushFeedback.type} className="py-2.5 px-3 mb-3 small">
                    {pushFeedback.message}
                  </Alert>
                )}

                <Form onSubmit={handleSendPushReminder}>
                  <Form.Group className="mb-3">
                    <Form.Label className="small fw-semibold text-muted">Target Recipients</Form.Label>
                    <Form.Select
                      value={pushData.target}
                      onChange={(e) => setPushData({ ...pushData, target: e.target.value })}
                      style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                    >
                      <option value="all">All Student Volunteers ({pushSubscribedCount} active devices)</option>
                      <option value="calendar_shifts">Upcoming Calendar Shifts (Tomorrow's Scheduled Volunteers & Leaders)</option>
                    </Form.Select>
                  </Form.Group>

                  {pushData.target === "calendar_shifts" ? (
                    <div
                      className="p-3 mb-4"
                      style={{
                        background: "var(--color-background-soft, #f8f9fa)",
                        borderRadius: "var(--radius-sm)",
                        border: "1px solid var(--color-border)",
                      }}
                    >
                      <div className="fw-semibold small text-primary mb-1">
                        📅 Automated Calendar-Based Shift Alerts
                      </div>
                      <div className="text-muted small mb-2">
                        Inspects all availability slots scheduled in active projects for tomorrow. Enrolled volunteers receive a personalized reminder with their activity, project name, date, start time, and location. Project leaders receive a briefing of scheduled volunteer counts.
                      </div>
                      <Form.Check
                        type="checkbox"
                        id="force-resend"
                        label="Force resend (bypass duplicate suppression)"
                        checked={pushData.forceResend || false}
                        onChange={(e) => setPushData({ ...pushData, forceResend: e.target.checked })}
                        className="small text-muted"
                      />
                    </div>
                  ) : (
                    <>
                      <Form.Group className="mb-3">
                        <Form.Label className="small fw-semibold text-muted">Notification Title</Form.Label>
                        <Form.Control
                          type="text"
                          placeholder="e.g. Volunteer Hours Submission Reminder"
                          value={pushData.title}
                          onChange={(e) => setPushData({ ...pushData, title: e.target.value })}
                          required
                          style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                        />
                      </Form.Group>

                      <Form.Group className="mb-4">
                        <Form.Label className="small fw-semibold text-muted">Notification Message</Form.Label>
                        <Form.Control
                          as="textarea"
                          rows={3}
                          placeholder="e.g. Friendly reminder to log all your volunteer hours for this week before Friday 17:00!"
                          value={pushData.message}
                          onChange={(e) => setPushData({ ...pushData, message: e.target.value })}
                          required
                          style={{ border: "1px solid var(--color-border)", borderRadius: "var(--radius-sm)" }}
                        />
                      </Form.Group>
                    </>
                  )}

                  <div className="d-flex justify-content-end gap-2">
                    <Button
                      variant="outline-secondary"
                      onClick={() => setShowPushModal(false)}
                      disabled={pushLoading}
                      style={{ borderRadius: "var(--radius-sm)" }}
                    >
                      Cancel
                    </Button>
                    <Button
                      type="submit"
                      disabled={
                        pushLoading ||
                        (pushData.target !== "calendar_shifts" && (!pushData.title.trim() || !pushData.message.trim()))
                      }
                      style={{
                        backgroundColor: "var(--color-primary)",
                        borderColor: "var(--color-primary)",
                        borderRadius: "var(--radius-sm)",
                      }}
                    >
                      {pushLoading ? (
                        <>
                          <Spinner size="sm" className="me-2" />
                          Processing…
                        </>
                      ) : pushData.target === "calendar_shifts" ? (
                        <>
                          <FiSend className="me-1" /> Scan & Dispatch Shift Reminders
                        </>
                      ) : (
                        <>
                          <FiSend className="me-1" /> Send Push Notification
                        </>
                      )}
                    </Button>
                  </div>
                </Form>
              </Modal.Body>
            </Modal>
          </Card.Body>
        </Card>
    </AppShell>
  );
};

export default AdminStudentsView;
